import { useTranslation } from "react-i18next";
import { cn } from "@core/utils";
import type { RoundStatus } from "../types";

/**
 * Per-status token triplet. Each status owns bg/border/text/dot vars in theme.css, so the
 * badge never reaches for a raw color — adding a lifecycle state is a token change plus one
 * row here, not a restyle.
 */
const STATUS_STYLES: Record<RoundStatus, string> = {
  DRAFT:
    "bg-(--status-draft-bg) border-(--status-draft-border) text-(--status-draft-text)",
  SCHEDULED:
    "bg-(--status-scheduled-bg) border-(--status-scheduled-border) text-(--status-scheduled-text)",
  OPEN: "bg-(--status-open-bg) border-(--status-open-border) text-(--status-open-text)",
  CLOSING_SOON:
    "bg-(--status-closing-soon-bg) border-(--status-closing-soon-border) text-(--status-closing-soon-text)",
  CLOSED:
    "bg-(--status-closed-bg) border-(--status-closed-border) text-(--status-closed-text)",
  // The later-lifecycle states. theme.css already ships tokens for all three — the designer
  // anticipated them — so they need no new colour, only a row here.
  FROZEN:
    "bg-(--status-frozen-bg) border-(--status-frozen-border) text-(--status-frozen-text)",
  TRANSFERRED_TO_SELECT:
    "bg-(--status-transferred-bg) border-(--status-transferred-border) text-(--status-transferred-text)",
  ARCHIVED:
    "bg-(--status-archived-bg) border-(--status-archived-border) text-(--status-archived-text)",
};

const STATUS_DOTS: Record<RoundStatus, string> = {
  DRAFT: "bg-(--status-draft-dot)",
  SCHEDULED: "bg-(--status-scheduled-dot)",
  OPEN: "bg-(--status-open-dot)",
  CLOSING_SOON: "bg-(--status-closing-soon-dot)",
  CLOSED: "bg-(--status-closed-dot)",
  FROZEN: "bg-(--status-frozen-dot)",
  TRANSFERRED_TO_SELECT: "bg-(--status-transferred-dot)",
  ARCHIVED: "bg-(--status-archived-dot)",
};

/** i18n key per status — kebab-case segments, `acquire` namespace. */
const STATUS_LABEL_KEYS = {
  DRAFT: "status.draft",
  SCHEDULED: "status.scheduled",
  OPEN: "status.open",
  CLOSING_SOON: "status.closing-soon",
  CLOSED: "status.closed",
  FROZEN: "status.frozen",
  TRANSFERRED_TO_SELECT: "status.transferred",
  ARCHIVED: "status.archived",
} as const;

/**
 * Round lifecycle pill (Figma "StatusBadge"): a dot plus the localized status name. Shared
 * across the module's features — the list card uses it today, the round-detail header next.
 *
 * The dot leads the label.
 */
export function StatusBadge({ status }: { status: RoundStatus }) {
  const { t } = useTranslation("acquire");

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border pl-2 pr-2.5 py-0.5",
        "text-xs-medium",
        STATUS_STYLES[status]
      )}
    >
      <span
        aria-hidden
        className={cn("size-1.5 shrink-0 rounded-full", STATUS_DOTS[status])}
      />
      {t(STATUS_LABEL_KEYS[status])}
    </span>
  );
}
